/**
 * Edpies — GCSE STEM Topic Browser (Seneca-style).
 *
 * Subject → Topic → Subtopic → {Learn, Practice, Mini-Mock}
 *
 * Pulls /api/stem-library/gcse-curriculum and renders a clean three-pane
 * navigation: subject pills along the top, exam-board + year-group chips,
 * an accordion of topics, each topic expands to its subtopics. Every
 * subtopic offers three actions:
 *   - Learn      → /learn/revision/<subject>/<slug>?difficulty=…
 *   - Practice   → /learn/practice/<subject>/<slug>?difficulty=…
 *   - Mini-Mock  → /learn/quiz/<subject>/<slug>?difficulty=…
 *
 * Year-group + exam-board filters re-fetch from the backend so the
 * displayed topic set always matches what's eligible.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, ChevronDown, ChevronRight, BookOpen, Target, Trophy,
  Atom, FlaskConical, Zap, Calculator, GraduationCap,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUBJECT_ICONS = { biology: Atom, chemistry: FlaskConical, physics: Zap, maths: Calculator };
const SUBJECT_ACCENTS = {
  biology: 'from-emerald-500 to-teal-600',
  chemistry: 'from-amber-500 to-orange-600',
  physics: 'from-sky-500 to-cyan-600',
  maths: 'from-violet-500 to-indigo-600',
};

const YEAR_GROUPS = [
  { id: 'year-9', label: 'Year 9' },
  { id: 'year-10', label: 'Year 10' },
  { id: 'year-11', label: 'Year 11' },
];
const EXAM_BOARDS = ['AQA', 'Edexcel', 'OCR'];

const detectYearGroupFromProfile = (user) => {
  const yg = String(user?.studentProfile?.yearGroup || user?.yearGroup || '').toLowerCase();
  if (/year[\s-]?9\b/.test(yg)) return 'year-9';
  if (/year[\s-]?10\b/.test(yg)) return 'year-10';
  if (/year[\s-]?11\b/.test(yg)) return 'year-11';
  return 'year-10';
};

const GcseStemBrowser = ({ progress }) => {
  const navigate = useNavigate();
  const initialYear = useMemo(() => detectYearGroupFromProfile(progress?.user), [progress]);
  const [yearGroup, setYearGroup] = useState(initialYear);
  const [examBoard, setExamBoard] = useState('AQA');
  const [activeSubject, setActiveSubject] = useState('biology');
  const [curriculum, setCurriculum] = useState(null);
  const [openTopics, setOpenTopics] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch curriculum whenever year-group or board changes.
  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/stem-library/gcse-curriculum`, {
        params: { year_group: yearGroup, exam_board: examBoard },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => setCurriculum(res.data?.subjects || {}))
      .catch(() => setCurriculum({}))
      .finally(() => setLoading(false));
  }, [yearGroup, examBoard]);

  const subjects = curriculum ? Object.values(curriculum) : [];
  const activeSubjectData = curriculum?.[activeSubject];

  const toggleTopic = (topicId) => {
    setOpenTopics((prev) => ({ ...prev, [topicId]: !prev[topicId] }));
  };

  const openSubtopic = (kind, subjectKey, slug) => {
    const difficulty = yearGroup === 'year-9' ? 'secure' : yearGroup === 'year-11' ? 'exam_ready' : 'greater_depth';
    navigate(`/learn/${kind}/${subjectKey}/${slug}?difficulty=${difficulty}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-white dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-16 pb-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/welcome')}
          className="mb-4 text-slate-600 dark:text-slate-300"
          data-testid="stem-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Home
        </Button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-6"
        >
          <p className="text-xs uppercase tracking-wider font-semibold text-cyan-700 dark:text-cyan-300">
            GCSE STEM Library
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mt-1">
            Seneca-style learning, practice & mocks
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5">
            Pick your subject, year group and exam board. Every topic expands into bite-size lessons with their own practice and mini-mock.
          </p>
        </motion.div>

        {/* Year-group + exam-board chips */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="stem-filters">
          <div className="flex items-center gap-1 mr-2" data-testid="year-group-chips">
            {YEAR_GROUPS.map((y) => (
              <button
                key={y.id}
                type="button"
                onClick={() => setYearGroup(y.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  yearGroup === y.id
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-700'
                }`}
                data-testid={`year-chip-${y.id}`}
              >
                {y.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1" data-testid="exam-board-chips">
            {EXAM_BOARDS.map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setExamBoard(b)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  examBoard === b
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-cyan-300 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-700'
                }`}
                data-testid={`board-chip-${b.toLowerCase()}`}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        {/* Subject pills */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="subject-pills">
          {subjects.map((sub) => {
            const Icon = SUBJECT_ICONS[sub.id] || GraduationCap;
            const active = activeSubject === sub.id;
            return (
              <button
                key={sub.id}
                type="button"
                onClick={() => setActiveSubject(sub.id)}
                className={`group inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition ${
                  active
                    ? `bg-gradient-to-br ${SUBJECT_ACCENTS[sub.id]} text-white border-transparent shadow-md`
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-700'
                }`}
                data-testid={`subject-pill-${sub.id}`}
              >
                <Icon className="h-4 w-4" />
                {sub.name.replace('GCSE ', '')}
                <Badge className={`ml-1 ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'} border-0`}>
                  {sub.topics?.length || 0}
                </Badge>
              </button>
            );
          })}
        </div>

        {/* Topic / subtopic accordion */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading curriculum…</div>
        ) : !activeSubjectData ? (
          <div className="text-center py-12 text-slate-500">No topics available for this filter.</div>
        ) : (
          <div className="space-y-3" data-testid="topics-accordion">
            {activeSubjectData.topics.map((topic) => {
              const isOpen = !!openTopics[topic.id];
              return (
                <Card
                  key={topic.id}
                  className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 overflow-hidden"
                  data-testid={`topic-${topic.id}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleTopic(topic.id)}
                    className="w-full text-left p-4 flex items-center gap-3 hover:bg-slate-50/60 dark:hover:bg-slate-800/60 transition"
                    data-testid={`topic-${topic.id}-toggle`}
                  >
                    <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${SUBJECT_ACCENTS[activeSubject]} text-white flex items-center justify-center font-bold shadow-sm shrink-0`}>
                      {topic.number}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-base font-bold text-slate-900 dark:text-white">
                        Topic {topic.number}: {topic.title}
                      </h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {topic.subtopics.length} subtopics · {topic.year_groups.map((y) => y.replace('year-', 'Y')).join(' · ')}
                      </p>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-950/40"
                      >
                        <CardContent className="p-3 sm:p-4 space-y-2">
                          {topic.subtopics.map((sub) => (
                            <div
                              key={sub.slug}
                              className="flex flex-wrap items-center gap-2 sm:gap-3 p-3 rounded-lg bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800"
                              data-testid={`subtopic-${sub.slug}`}
                            >
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex-1 min-w-[150px]">
                                {sub.title}
                              </p>
                              <div className="flex items-center gap-1.5">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openSubtopic('revision', activeSubject, sub.slug)}
                                  className="h-8 px-2.5 text-xs gap-1"
                                  data-testid={`subtopic-${sub.slug}-learn`}
                                >
                                  <BookOpen className="h-3.5 w-3.5" /> Learn
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openSubtopic('practice', activeSubject, sub.slug)}
                                  className="h-8 px-2.5 text-xs gap-1"
                                  data-testid={`subtopic-${sub.slug}-practice`}
                                >
                                  <Target className="h-3.5 w-3.5" /> Practice
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => openSubtopic('quiz', activeSubject, sub.slug)}
                                  className="h-8 px-2.5 text-xs gap-1 bg-violet-600 hover:bg-violet-700 text-white"
                                  data-testid={`subtopic-${sub.slug}-mock`}
                                >
                                  <Trophy className="h-3.5 w-3.5" /> Mini-Mock
                                </Button>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GcseStemBrowser;
